import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  People,
  Assignment,
  Warning,
  CheckCircle,
  Add,
  ViewList,
  ArrowForward,
  Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { clientApi, filingApi } from '../services/api';
import { differenceInDays, set } from 'date-fns';

interface Client {
  id: string;
  clientName: string;
  clientEmail: string;
  hstFrequency: string;
  periodEndDate: string;
  dueDate: string;
  status: string;
  currentFilingStatus?: string; 
  employeeAssigned: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [filedCount, setFiledCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canAddClients = user.role === 'ADMIN' || user.role === 'SUPER_ADMIN';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch clients and filing stats
      const [clientsResponse, statsResponse] = await Promise.all([
        clientApi.getAll(),
        filingApi.getStats(),
      ]);
      
      // Filter out INACTIVE clients
      const activeClients = (clientsResponse.data.data || []).filter((c: Client) => {
        const status = c.status?.toUpperCase();
        return status !== 'INACTIVE';
      });
      setClients(activeClients);
      setPendingCount(statsResponse.data.data.pendingCount || 0);
      setOverdueCount(statsResponse.data.data.overdueCount || 0);
      setFiledCount(statsResponse.data.data.filedCount || 0);
    } catch (error) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const stats = [
    {
      title: 'Active Clients',
      value: clients.length,
      icon: <People sx={{ fontSize: 24 }} />,
      color: '#10B981',
      bgColor: '#ECFDF5',
      onClick: () => navigate('/clients'),
    },
    {
      title: 'Pending Filings',
      value: pendingCount,
      icon: <Assignment sx={{ fontSize: 24 }} />,
      color: '#F59E0B',
      bgColor: '#FEF3C7',
      onClick: () => navigate('/filings?status=PENDING'),
    },
    {
      title: 'Overdue Filings',
      value: overdueCount,
      icon: <Warning sx={{ fontSize: 24 }} />,
      color: '#EF4444',
      bgColor: '#FEF2F2',
      onClick: () => navigate('/filings?status=OVERDUE'),
    },
    {
      title: 'Filed Filings',
      value: filedCount,
      icon: <CheckCircle sx={{ fontSize: 24 }} />,
      color: '#059669',
      bgColor: '#D1FAE5',
      onClick: () => navigate('/filings?status=FILED'),
    },
  ];

  // Action Required - Overdue + Due in next 7 days
// Action Required - Only OVERDUE clients with their overdue filing counts
  const [overdueFilings, setOverdueFilings] = useState<any[]>([]);
  const [overdueFilingsCount, setOverdueFilingsCount] = useState(0);

  useEffect(() => {
    if (clients.length > 0) {
      // Fetch all overdue filings to get exact counts per client
      fetchOverdueFilings();
    }
  }, [clients]);

  const fetchOverdueFilings = async () => {
    try {
      const response = await filingApi.getAll();
      const allFilings = response.data.data || [];
      
      // Filter to only OVERDUE filings with non-inactive clients
      const overdueOnly = allFilings.filter((f: any) => 
        f.status === 'OVERDUE' && 
        f.client?.status !== 'INACTIVE'
      );
      
      setOverdueFilingsCount(overdueOnly.length);
      
      // Group by client and count overdue filings per client
      const clientOverdueCounts: { [key: string]: { client: any; count: number; filings: any[] } } = {};
      
      overdueOnly.forEach((filing: any) => {
        const clientId = filing.client?.id;
        if (clientId) {
          if (!clientOverdueCounts[clientId]) {
            clientOverdueCounts[clientId] = {
              client: filing.client,
              count: 0,
              filings: []
            };
          }
          clientOverdueCounts[clientId].count++;
          clientOverdueCounts[clientId].filings.push(filing);
        }
      });
      
      // Convert to array and sort by count (highest first)
      const overdueClientsWithCounts = Object.values(clientOverdueCounts)
        .sort((a, b) => b.count - a.count);
      
      setOverdueFilings(overdueClientsWithCounts);
    } catch (error) {
      console.error('Error fetching overdue filings:', error);
    }
  };

  // Upcoming Deadlines - Next 14 days (excluding action required items)
  const upcomingDeadlines = clients
    .filter((c) => {
      const daysLeft = differenceInDays(new Date(c.dueDate), new Date());
      return daysLeft > 7 && daysLeft <= 14 && c.currentFilingStatus !== 'OVERDUE';
    })
    .map((client) => ({
      ...client,
      daysLeft: differenceInDays(new Date(client.dueDate), new Date()),
    }))
    .sort((a, b) => a.daysLeft - b.daysLeft);

  const getUrgencyColor = (urgency: 'overdue' | 'urgent') => {
    return urgency === 'overdue' ? '#EF4444' : '#F59E0B';
  };

  const getUrgencyBgColor = (urgency: 'overdue' | 'urgent') => {
    return urgency === 'overdue' ? '#FEF2F2' : '#FEF3C7';
  };

  const getUrgencyLabel = (urgency: 'overdue' | 'urgent') => {
    return urgency === 'overdue' ? 'OVERDUE' : 'URGENT';
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress sx={{ color: '#667eea' }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: '2rem',
              mb: 1,
              color: '#1a1a1a',
            }}
          >
            Dashboard
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#666666',
              fontSize: '1rem',
            }}
          >
            Overview of your HST/GST filings and deadlines
          </Typography>
        </Box>
        <Button
          startIcon={<Refresh />}
          onClick={fetchData}
          sx={{
            textTransform: 'none',
            color: '#667eea',
            border: '1px solid #E5E7EB',
            fontWeight: 600,
            px: 3,
            py: 1.25,
            '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.08)', borderColor: '#667eea' },
          }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Action Required Alert */}
          {overdueFilings.length > 0 && (
              <Alert
                severity="error"
                icon={false}
                sx={{
                  mb: 4,
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FCA5A5',
                }}
              >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#991B1B', mb: 0.5 }}>
                Action Required
              </Typography>
              <Typography variant="body2" sx={{ color: '#7F1D1D' }}>
                {overdueFilingsCount} overdue filing{overdueFilingsCount !== 1 ? 's' : ''} requiring immediate attention
              </Typography>
            </Box>
            <Button
              endIcon={<ArrowForward />}
              onClick={() => navigate('/filings?status=OVERDUE')}
              sx={{
                textTransform: 'none',
                color: '#EF4444',
                border: '1px solid #EF4444',
                fontWeight: 600,
                '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.08)' },
              }}
            >
              View All
            </Button>
          </Box>
        </Alert>
      )}

      {/* Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 3,
          mb: 4,
        }}
      >
        {stats.map((stat, index) => (
          <Card
            key={index}
            elevation={0}
            onClick={stat.onClick}
            sx={{
              border: '1px solid #E5E7EB',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: stat.color,
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6B7280',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      mb: 1,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {stat.title}
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      fontSize: '2.25rem',
                      color: stat.color,
                      lineHeight: 1,
                    }}
                  >
                    {stat.value}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    backgroundColor: stat.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: stat.color,
                  }}
                >
                  {stat.icon}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Action Required Section */}
      {overdueFilings.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #E5E7EB',
            overflow: 'hidden',
            mb: 4,
          }}
        >
        
          <Box
            sx={{
              p: 3,
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#FEF2F2',
            }}
          >
          <Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.125rem',
                  color: '#991B1B',
                }}
              >
                Action Required
              </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#7F1D1D',
                    fontSize: '0.875rem',
                  }}
                >
                  Overdue and urgent filings that need immediate attention
                </Typography>
              </Box>
            </Box>
            <Button
              endIcon={<ArrowForward />}
              onClick={() => navigate('/filings?status=OVERDUE')}
              sx={{
                color: '#EF4444',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.08)',
                },
              }}
            >
              View All
            </Button>
          </Box>

          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {overdueFilings.slice(0, 5).map((item) => (
                <Box
                  key={item.client.id}
                  sx={{
                    p: 3,
                    border: '1px solid #E5E7EB',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#EF4444',
                      backgroundColor: '#F9FAFB',
                    },
                  }}
                  onClick={() => navigate(`/clients/${item.client.id}`)}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        fontSize: '1rem',
                        mb: 0.5,
                        color: '#111827',
                      }}
                    >
                      {item.client.clientName}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#6B7280',
                        fontSize: '0.875rem',
                      }}
                    >
                      {item.client.hstFrequency}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.5rem',
                          color: '#EF4444',
                          lineHeight: 1,
                          mb: 0.5,
                        }}
                      >
                        {item.count}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6B7280',
                          fontSize: '0.75rem',
                        }}
                      >
                        overdue filing{item.count !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    <Chip
                      label="OVERDUE"
                      size="small"
                      sx={{
                        backgroundColor: '#FEF2F2',
                        color: '#EF4444',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 28,
                        border: '1px solid #EF4444',
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      )}

      {/* Upcoming Deadlines */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #E5E7EB',
          overflow: 'hidden',
          mb: 4,
        }}
      >
        <Box
          sx={{
            p: 3,
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '1.125rem',
                mb: 0.5,
                color: '#111827',
              }}
            >
              Upcoming Deadlines
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#6B7280',
                fontSize: '0.875rem',
              }}
            >
              Filings due in the next 8-14 days
            </Typography>
          </Box>
          <Button
            endIcon={<ArrowForward />}
            onClick={() => navigate('/clients')}
            sx={{
              color: '#667eea',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.08)',
              },
            }}
          >
            View All
          </Button>
        </Box>

        <Box sx={{ p: 3 }}>
          {upcomingDeadlines.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: '#6B7280',
                  mb: 1,
                }}
              >
                No upcoming deadlines in the next 8-14 days
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#9CA3AF',
                  fontSize: '0.875rem',
                }}
              >
                You're all caught up! 🎉
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {upcomingDeadlines.slice(0, 5).map((client) => (
                <Box
                  key={client.id}
                  sx={{
                    p: 3,
                    border: '1px solid #E5E7EB',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#667eea',
                      backgroundColor: '#F9FAFB',
                    },
                  }}
                  onClick={() => navigate(`/clients/${client.id}`)}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        fontSize: '1rem',
                        mb: 0.5,
                        color: '#111827',
                      }}
                    >
                      {client.clientName}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#6B7280',
                        fontSize: '0.875rem',
                      }}
                    >
                      Due: {new Date(client.dueDate).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: '2-digit', 
                        year: 'numeric',
                        timeZone: 'UTC' 
                      })} • {client.hstFrequency}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.5rem',
                          color: '#667eea',
                          lineHeight: 1,
                          mb: 0.5,
                        }}
                      >
                        {client.daysLeft}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6B7280',
                          fontSize: '0.75rem',
                        }}
                      >
                        days left
                      </Typography>
                    </Box>
                    <Chip
                      label="UPCOMING"
                      size="small"
                      sx={{
                        backgroundColor: '#EEF2FF',
                        color: '#667eea',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 28,
                        border: '1px solid #C7D2FE',
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Quick Actions */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #E5E7EB',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB' }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '1.125rem',
              color: '#111827',
            }}
          >
            Quick Actions
          </Typography>
        </Box>
        <Box sx={{ p: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {canAddClients && (
            <Button
              startIcon={<Add />}
              onClick={() => navigate('/clients/new')}
              sx={{
                textTransform: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#FFFFFF',
                fontWeight: 600,
                px: 3,
                py: 1.25,
                '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)' },
              }}
            >
              Add New Client
            </Button>
          )}
          <Button
            startIcon={<ViewList />}
            onClick={() => navigate('/clients')}
            sx={{
              textTransform: 'none',
              color: '#667eea',
              border: '1px solid #667eea',
              fontWeight: 600,
              px: 3,
              py: 1.25,
              '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.08)' },
            }}
          >
            View All Clients
          </Button>
          <Button
            startIcon={<Assignment />}
            onClick={() => navigate('/filings')}
            sx={{
              textTransform: 'none',
              color: '#667eea',
              border: '1px solid #667eea',
              fontWeight: 600,
              px: 3,
              py: 1.25,
              '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.08)' },
            }}
          >
            View All Filings
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Dashboard;